import * as t from "io-ts";
import { ControlV } from "./controls";
import { TreeNode } from "@/types/tree";
import { DynamicPropertyPathV } from "@/types/dynamic-property-path";
import { Either } from "fp-ts/lib/Either";

export const BuilderElementControlsV = t.exact(
  t.type({
    contentSections: t.array(ControlV),
    designSections: t.array(ControlV),
    settingsSections: t.array(ControlV)
  })
);

export type BuilderElementControls = t.TypeOf<typeof BuilderElementControlsV>;

export const BuilderElementAttributeTemplatedV = t.type({
  name: t.string,
  template: t.string
});

export const BuilderElementAttributePropertyPathBasedV = t.type({
  name: t.string,
  propertyPath: t.string
});

export const BuilderElementAttributeWithRawValueV = t.type({
  name: t.string,
  rawValue: t.string
});

export type BuilderElementAttributeTemplated = t.TypeOf<
  typeof BuilderElementAttributeTemplatedV
>;

export type BuilderElementAttributePropertyPathBased = t.TypeOf<
  typeof BuilderElementAttributePropertyPathBasedV
>;

export type BuilderElementAttributeWithRawValue = t.TypeOf<
  typeof BuilderElementAttributeWithRawValueV
>;

export const BuilderElementAttributeV = t.union([
  BuilderElementAttributeTemplatedV,
  BuilderElementAttributePropertyPathBasedV,
  BuilderElementAttributeWithRawValueV
]);

export type BuilderElementAttribute = t.TypeOf<typeof BuilderElementAttributeV>;

export const BuilderElementAttributesV = t.array(BuilderElementAttributeV);

export type BuilderElementAttributes = t.TypeOf<
  typeof BuilderElementAttributesV
>;

export const BuilderElementAdditionalClassV = t.type({
  name: t.string,
  template: t.string
});

export type BuilderElementAdditionalClass = t.TypeOf<
  typeof BuilderElementAdditionalClassV
>;

export const BuilderElementAdditionalClassesV = t.array(
  BuilderElementAdditionalClassV
);

export type BuilderElementAdditionalClasses = t.TypeOf<
  typeof BuilderElementAdditionalClassesV
>;

export const BuilderElementDefaultPropertiesV =
  t.unknown; /* TODO: make this a real type */

export type BuilderElementDefaultProperties = t.TypeOf<
  typeof BuilderElementDefaultPropertiesV
>;

const BuilderActionDataV = t.intersection([
  t.type({ script: t.string }),
  t.partial({ dependencies: t.array(t.string), runForAllChildren: t.boolean })
]);

const BuilderActionV = t.type({
  actionData: BuilderActionDataV,
  elements: t.array(t.string)
});

export type BuilderActionData = t.TypeOf<typeof BuilderActionDataV>;
export type BuilderAction = t.TypeOf<typeof BuilderActionV>;

export const ElementDependencyAndConditionV = t.partial({
  title: t.string,
  // src scripts
  scripts: t.array(t.string),
  // js code added to head
  inlineScripts: t.array(t.string),
  // file styles
  styles: t.array(t.string),
  // css added to head
  inlineStyles: t.array(t.string),
  // if present, dependencies run if condition is met
  builderCondition: t.string,
  // if present, dependencies run if condition is met
  frontendCondition: t.string,
  // Google fonts to import
  googleFonts: t.array(t.string)
});

const DependencyFiles = t.array(
  t.type({
    name: t.string,
    url: t.string
  })
);

export const AvailableDependencyFilesV = t.type({
  name: t.string,
  files: t.type({
    js: DependencyFiles,
    css: DependencyFiles
  })
});

export type AvailableDependencyFiles = t.TypeOf<
  typeof AvailableDependencyFilesV
>;

export const BuilderActionsV = t.partial({
  onPropertyChange: t.array(BuilderActionDataV),
  onAfterDeletedElement: t.array(BuilderActionDataV),
  onMovedElement: t.array(BuilderActionDataV),
  onMountedElement: t.array(BuilderActionDataV),
  onCreatedElement: t.array(BuilderActionDataV),
  onBeforeDeletingElement: t.array(BuilderActionDataV),
  onActivatedElement: t.array(BuilderActionDataV)
});

export type BuilderActions = t.TypeOf<typeof BuilderActionsV>;
export type BuilderActionNames = keyof BuilderActions;

export type ElementDependencyAndCondition = t.TypeOf<
  typeof ElementDependencyAndConditionV
>;

export const NestingRuleTypeV = t.union([
  // root: the root of the entire page. Only the Tree's root element. All elements go inside it.
  t.literal("root"),

  // final: nothing can be inside of it. It's the final element.
  // (like a text, header, or most of the fancy elements)
  t.literal("final"),

  // final-link: like final but it's a link. Important because we can't have links inside links  that's invalid HTML),
  // so final-link cannot be inside container-link
  t.literal("final-link"),

  // container: accept any element as children except section
  t.literal("container"),

  // container-restricted: only accepts elements that are registered to be its children  with restrictedToBeADirectChildOf.
  // Powerful to enable only certain elements inside other elements.
  // E.g: Slide can only be inside Slider; Tab only inside Tabs
  t.literal("container-restricted"),

  // container-link: a container that's a link. Accepts everything a container does minus final-link
  t.literal("container-link"),

  // container-excluding-self: a container that can't include itself. E.g "product builder" can't be inside "product builder"
  t.literal("container-excluding-self"),

  // section: like container but can only be a direct child of root
  t.literal("section"),

  // final-that-is-allowed-inside-root: an element that can be a direct descendant of root
  // (aka not wrapped around a section) and that doesn't accept any children.
  t.literal("final-that-is-allowed-inside-root"),

  // popup: can be a direct child of root, but only on breakdance_popup post type
  t.literal("popup")
]);

export type NestingRuleType = t.TypeOf<typeof NestingRuleTypeV>;

export type LegalPlaceToAddElementInTree =
  | "TOP_LEVEL"
  | { index: false | number; place: TreeNode };

export type NoPlaceToAddElementInTree =
  | "NONE"
  | "NOT_ALLOWED_BECAUSE_OF_OTHER_NODES";

export type PlaceToAddElementInTree = Either<
  NoPlaceToAddElementInTree,
  LegalPlaceToAddElementInTree
>;

export type CustomDocumentPostTypes =
  | "breakdance_block"
  | "breakdance_acf_block"
  | "breakdance_popup";

export const BuilderElementNestingRuleV = t.intersection([
  t.type({
    type: NestingRuleTypeV
  }),
  t.partial({
    restrictedToBeADirectChildOf: t.array(t.string),
    restrictedToBeADescendantOf: t.array(t.string),
    notAllowedWhenNodeTypeIsPresentInTree: t.array(t.string)
  })
]);

export type BuilderElementNestingRule = t.TypeOf<
  typeof BuilderElementNestingRuleV
>;

/**
 * It's important that this and SpacingBarLocationV stay in sync and in order
 */
export const SpacingBarLocationV = t.union([
  t.literal("inside-top"),
  t.literal("inside-left"),
  t.literal("inside-right"),
  t.literal("inside-bottom"),
  t.literal("outside-top"),
  t.literal("outside-right"),
  t.literal("outside-left"),
  t.literal("outside-bottom")
]);

export const SpacingBarCssProperty = t.union([
  t.literal("padding-top"),
  t.literal("padding-left"),
  t.literal("padding-right"),
  t.literal("padding-bottom"),
  t.literal("margin-top"),
  t.literal("margin-right"),
  t.literal("margin-left"),
  t.literal("margin-bottom")
]);

/**/

const PropertyPathsPropertyV = t.union([t.literal(false), t.array(t.string)]);

export type PropertyPathsProperty = t.TypeOf<typeof PropertyPathsPropertyV>;

export type SpacingBarLocation = t.TypeOf<typeof SpacingBarLocationV>;

export type SpacingBarProperty = t.TypeOf<typeof SpacingBarCssProperty>;

export const SpacingBarDescriptionV = t.type({
  location: SpacingBarLocationV,
  cssProperty: SpacingBarCssProperty,
  affectedPropertyPath: t.string
});

export type SpacingBarDescription = t.TypeOf<typeof SpacingBarDescriptionV>;

export type DefaultChildElementInterface = {
  slug: string;
  defaultProperties?: BuilderElementDefaultProperties;
  children?: DefaultChildElementInterface[];
};

export const DefaultChildElementV: t.Type<DefaultChildElementInterface> =
  t.recursion("ChildElement", () =>
    t.intersection([
      t.type({
        slug: t.string
      }),
      t.partial({
        defaultProperties: BuilderElementDefaultPropertiesV,
        children: t.array(DefaultChildElementV)
      })
    ])
  );

export type DefaultChildElement = t.TypeOf<typeof DefaultChildElementV>;

export const RequiredPluginV = t.union([
  t.literal("WooCommerce"),
  t.literal("ACF"),
  t.literal("MetaBox"),
  t.literal("ACF"),
  t.literal("FacetWP"),
  t.literal("WP Grid Builder")
]);
export type RequiredPlugin = t.TypeOf<typeof RequiredPluginV>;

const ElementSettingsV = t.union([
  t.partial({
    dependsOnGlobalScripts: t.boolean,
    bypassPointerEvents: t.boolean,
    sharePropsWithSSRChildren: t.boolean,
    requiredPlugins: t.array(RequiredPluginV),
    disableAI: t.boolean,
    proOnly: t.boolean
  }),
  t.literal(false)
]);
export type ElementSettings = t.TypeOf<typeof ElementSettingsV>;

export const AddPanelRulesV = t.union([
  t.literal(false),
  t.partial({
    alwaysHide: t.boolean,
    allowedPostTypes: t.array(t.string)
  })
]);

export type AddPanelRules = t.TypeOf<typeof AddPanelRulesV>;

export const BuilderElementProjectManagementValueV = t.union([
  t.literal("yes"),
  t.literal("no"),
  t.literal("unknown"),
  t.literal("sort of")
]);

export const BuilderElementProjectManagementV = t.partial({
  looksGood: BuilderElementProjectManagementValueV,
  optionsGood: BuilderElementProjectManagementValueV,
  optionsWork: BuilderElementProjectManagementValueV,
  dynamicBehaviorWorks: BuilderElementProjectManagementValueV,
  notes: t.string
});

export type BuilderElementProjectManagement = t.TypeOf<
  typeof BuilderElementProjectManagementV
>;
export type BuilderElementProjectManagementValue = t.TypeOf<
  typeof BuilderElementProjectManagementValueV
>;

export const BadgeV = t.type({
  backgroundColor: t.union([t.string, t.null]),
  textColor: t.union([t.string, t.null]),
  label: t.string
});

export type Badge = t.TypeOf<typeof BadgeV>;

export const BuilderElementV = t.exact(
  t.type({
    name: t.string,
    className: t.union([t.string, t.literal(false)]),
    uiIcon: t.union([t.string, t.literal(false)]),
    slug: t.string,
    category: t.string,
    badge: t.union([BadgeV, t.literal(false)]),
    htmlTag: t.type({
      default: t.string,
      options: t.array(t.string),
      pathToControl: t.union([t.literal(false), t.string])
    }),
    template: t.string,
    additionalClasses: t.union([
      BuilderElementAdditionalClassesV,
      t.literal(false)
    ]),
    attributes: t.union([BuilderElementAttributesV, t.literal(false)]),
    cssTemplate: t.string,
    defaultCss: t.string,
    defaultProperties: t.union([
      BuilderElementDefaultPropertiesV,
      t.literal(false)
    ]),
    defaultChildren: t.union([t.array(DefaultChildElementV), t.literal(false)]),
    controls: BuilderElementControlsV,
    dependencies: t.union([
      t.array(ElementDependencyAndConditionV),
      t.literal(false)
    ]),
    actions: t.union([BuilderActionsV, t.literal(false)]),
    nestingRule: BuilderElementNestingRuleV,
    spacingBars: t.union([t.literal(false), t.array(SpacingBarDescriptionV)]),
    dynamicPropertyPaths: t.union([
      t.literal(false),
      t.array(DynamicPropertyPathV)
    ]),
    settings: ElementSettingsV,
    addPanelRules: AddPanelRulesV,
    experimental: t.boolean,
    order: t.number,
    projectManagement: t.union([
      t.literal(false),
      BuilderElementProjectManagementV
    ]),
    propertyPathsToWhitelistInFlatProps: PropertyPathsPropertyV,
    propertyPathsToSsrElementWhenValueChanges: PropertyPathsPropertyV
  })
);

export type BuilderElement = t.TypeOf<typeof BuilderElementV>;

export const BuilderElementsArrayV = t.array(BuilderElementV);

export type BuilderElements = Record<
  BuilderElement["slug"],
  BuilderElement | undefined
>;

export const BuilderElementCategoryV = t.type({
  slug: t.string,
  label: t.string
});
export const BuilderElementCategoriesV = t.array(BuilderElementCategoryV);

export type BuilderElementCategory = t.TypeOf<typeof BuilderElementCategoryV>;

export const ElementsExternalAttrsDepsAndActionsV = t.partial({
  attributes: t.array(BuilderElementAttributeV),
  dependencies: t.array(ElementDependencyAndConditionV),
  actions: BuilderActionsV
});

export type ElementsExternalAttrsDepsAndActions = t.TypeOf<
  typeof ElementsExternalAttrsDepsAndActionsV
>;

export const MacrosDataV = t.array(
  t.type({
    macro: t.string,
    slug: t.string
  })
);

export type MacrosData = t.TypeOf<typeof MacrosDataV>;
